/**
 * Property Slide-over Alpine.js Component
 *
 * Manages the property detail slide-over panel state and data fetching.
 * Follows the same pattern as tenancy_slideover.js.
 */

import consumer from "../channels/consumer"
import { notesMixin, destroyNote } from './notes_mixin'
import * as compliance from '../utils/compliance_status'
import * as contactGroup from '../utils/contact_group'
import { pill } from '../utils/pills'

// Lazy modal loading — shared across all property slideover instances.
// Modals are fetched once, injected outside the turbo-frame, and persist
// across all Turbo Frame navigations for the entire browser session.
let _modalsLoaded = false
let _modalsLoading = null

function ensureModalsLoaded() {
  if (_modalsLoaded) return Promise.resolve()
  if (_modalsLoading) return _modalsLoading
  _modalsLoading = fetchAndInjectModals()
  return _modalsLoading
}

async function fetchAndInjectModals() {
  const container = document.getElementById('property-slideover-modals-container')
  if (!container) return

  // Already populated (e.g. from a previous load)
  if (container.children.length > 0) {
    _modalsLoaded = true
    return
  }

  const maxRetries = 2
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch('/properties/slideover_modals', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const html = await response.text()
      container.innerHTML = html

      // Evaluate inline scripts (landlordOwnershipModal, manageMetersModal, addReadingModal)
      container.querySelectorAll('script').forEach(oldScript => {
        const newScript = document.createElement('script')
        Array.from(oldScript.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value))
        newScript.textContent = oldScript.textContent
        oldScript.replaceWith(newScript)
      })

      // Initialize Alpine components on the injected elements
      if (typeof Alpine !== 'undefined') {
        container.querySelectorAll('[x-data]').forEach(el => {
          if (!el._x_dataStack) Alpine.initTree(el)
        })
      }

      _modalsLoaded = true
      return
    } catch (error) {
      console.error(`[PropertySlideover] Failed to load modals (attempt ${attempt + 1}):`, error)
      if (attempt === maxRetries) _modalsLoading = null
    }
  }
}

export { ensureModalsLoaded }

export function propertySlideover() {
  return {
    ...notesMixin,
    // State
    open: false,
    loading: false,
    error: null,
    property: null,
    propertyId: null,
    subscription: null,
    zIndex: 100010,

    // Tab state
    activeTab: 'overview',

    // History tab state
    noteForm: { content: '', saving: false },

    // The Lettings tab's "New" menu: a valuation, an instruction or a tenancy
    newMenuOpen: false,

    // Utilities tab state
    activeMeterTab: 'gas',
    expandedSupplierType: null,
    utilitySuggestions: {},
    supplierTypes: [
      { key: 'gas', label: 'Gas', icon: 'ph-flame' },
      { key: 'electricity', label: 'Electricity', icon: 'ph-lightning' },
      { key: 'water', label: 'Water', icon: 'ph-drop' },
      { key: 'council_tax', label: 'Council Tax', icon: 'ph-bank' }
    ],

    // Documents tab state
    documentsForm: {
      dragging: false,
      documentsToDelete: [],
      saving: false
    },

    // EPC chart
    epcChartHtml: '',

    // Kebab menu state
    kebabOpen: false,

    // AI panel state
    aiPanelOpen: false,

    // Initialize component
    init() {
      const self = this

      // Preload modals during idle time so they're ready before the user opens the slideover
      const preload = () => ensureModalsLoaded()
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(preload)
      } else {
        setTimeout(preload, 2000)
      }

      // Subscribe to ActionCable for real-time updates
      this.subscription = consumer.subscriptions.create(
        { channel: "DataUpdatesChannel" },
        {
          received(message) {
            self.handleDataUpdate(message)
          }
        }
      )

      // Refresh when landlord ownership changes
      window.addEventListener('landlord-ownership-updated', (e) => {
        if (this.property && String(this.property.id) === String(e.detail.propertyId)) {
          this.fetchProperty(true)
        }
      })

      // Update cached EPC data when EPC is added/removed via modal
      window.addEventListener('epc-updated', (e) => {
        if (this.property && String(this.property.id) === String(e.detail.propertyId)) {
          if (e.detail.action === 'destroy') {
            this.property.epc = null
          }
          this.fetchProperty(true)
        }
      })

      // Track AI panel state
      window.addEventListener('ai-panel-toggled', (e) => {
        this.aiPanelOpen = e.detail.open
      })

      // The Manage Photos modal uploads photos via its own fetch (no create
      // broadcast), so an already-open slideover holds a stale gallery cache.
      // Invalidate it and refresh hero/count so the new photos appear without
      // a page reload.
      window.addEventListener('property-photos-updated', (e) => {
        if (this.property && String(this.property.id) === String(e.detail.propertyId)) {
          this._photoUrls = null
          this.fetchProperty(true)
        }
      })
    },

    // Clean up when component is removed from DOM (e.g. Turbo navigation)
    destroy() {
      if (this.subscription) {
        this.subscription.unsubscribe()
        this.subscription = null
      }
      const store = Alpine.store?.('slideovers')
      if (store && this.propertyId) {
        store.unregister('property', this.propertyId)
      }
    },

    // Open slide-over for a specific property
    openForProperty(propertyId, tab) {
      ensureModalsLoaded()
      const store = Alpine.store?.('slideovers')

      // If already open in this component, bring to front
      if (store?.isOpen?.('property', propertyId) && this.open && this.propertyId === propertyId) {
        store.bringToFront('property', propertyId)
        if (tab) this.activeTab = tab
        return
      }

      // Clean up stale registration (e.g. after page navigation)
      if (store?.isOpen?.('property', propertyId)) {
        store.unregister('property', propertyId)
      }

      // Get next z-index from store
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }

      this.propertyId = propertyId
      this.property = null
      this.error = null
      this.activeTab = this.resolveTab(tab)
      this.noteForm = { content: '', saving: false }
      this.activeMeterTab = 'gas'
      this.expandedSupplierType = null
      this.utilitySuggestions = {}
      this.kebabOpen = false
      this.newMenuOpen = false
      this._photoUrls = null
      this.open = true

      // Record this property view in localStorage for 'Recently Viewed' sorting
      try {
        const key = 'recentlyViewedPropertyIds'
        let ids = JSON.parse(localStorage.getItem(key) || '[]')
        ids = ids.filter(id => id !== propertyId)
        ids.unshift(propertyId)
        if (ids.length > 50) ids.length = 50
        localStorage.setItem(key, JSON.stringify(ids))
      } catch (e) { /* localStorage unavailable */ }

      // Register this slideover
      if (store?.register) {
        store.register('property', propertyId, this)
      }

      // An inspection booked for this property (shared modal) - refresh
      window.addEventListener('inspection-booked', (event) => {
        if (this.property && event.detail && event.detail.propertyId === this.property.id) {
          this.fetchProperty(true)
        }
      })

      this.fetchProperty()
    },

    // Close slide-over
    close() {
      const store = Alpine.store('slideovers')
      if (store && this.propertyId) {
        store.unregister('property', this.propertyId)
      }
      this.open = false
    },

    // Fetch property data from server
    async fetchProperty(silent = false) {
      if (!this.propertyId) return

      if (!silent) {
        this.loading = true
      }
      this.error = null

      try {
        const response = await fetch(`/properties/${this.propertyId}.json`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        const data = await response.json()

        if (silent && this.property) {
          // Preserve tab state on silent refresh
          Object.assign(this.property, data)
        } else {
          this.property = data
          // Default meter tab to first type that has meters
          const meters = data.meters || []
          const firstWithData = ['gas', 'water', 'electric'].find(type =>
            meters.some(m => m.meter_type === type || (type === 'electric' && m.meter_type === 'electricity'))
          )
          if (firstWithData) this.activeMeterTab = firstWithData
        }

        // Likely suppliers for unrecorded utilities (no-op when all recorded)
        this.fetchUtilitySuggestions()

        // Fetch EPC chart if property has EPC data — on silent refreshes too,
        // so the chart appears as soon as an EPC is attached via the modal
        if (data.epc?.current_score && data.epc?.current_rating && !data.epc?.expired) {
          this.fetchEpcChart(data.id)
        } else {
          this.epcChartHtml = ''
        }
      } catch (error) {
        console.error('[PropertySlideover] Failed to fetch property:', error)
        if (!silent) {
          this.error = 'Failed to load property details. Please try again.'
        }
      } finally {
        this.loading = false
      }
    },

    // ========== Documents (grid with upload/delete) ==========

    addDocuments(files) {
      const fileArray = Array.from(files).filter(f =>
        f.type === 'application/pdf' || f.type.startsWith('image/')
      )
      if (fileArray.length === 0) return
      this.uploadDocuments(fileArray)
    },

    async uploadDocuments(files) {
      this.documentsForm.saving = true
      try {
        const formData = new FormData()
        for (const file of files) {
          formData.append('documents[]', file)
        }

        const response = await fetch(`/properties/${this.propertyId}/update_documents`, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: formData
        })

        if (response.ok) {
          await this.fetchProperty(true)
          this.pollForDocumentThumbnails()
        } else {
          const data = await response.json().catch(() => ({}))
          alert(data.error || 'Failed to upload documents')
        }
      } catch (error) {
        console.error('Failed to upload documents:', error)
        alert('Failed to upload documents. Please try again.')
      } finally {
        this.documentsForm.saving = false
      }
    },

    toggleDocumentDeletion(docId) {
      const index = this.documentsForm.documentsToDelete.indexOf(docId)
      if (index > -1) {
        this.documentsForm.documentsToDelete.splice(index, 1)
      } else {
        this.documentsForm.documentsToDelete.push(docId)
      }
    },

    async saveDocuments() {
      if (this.documentsForm.documentsToDelete.length === 0) return

      this.documentsForm.saving = true
      try {
        const formData = new FormData()
        for (const id of this.documentsForm.documentsToDelete) {
          formData.append('documents_to_delete[]', id)
        }

        const response = await fetch(`/properties/${this.propertyId}/update_documents`, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: formData
        })

        if (response.ok) {
          await this.fetchProperty(true)
          this.documentsForm.documentsToDelete = []
        } else {
          const data = await response.json().catch(() => ({}))
          alert(data.error || 'Failed to delete documents')
        }
      } catch (error) {
        console.error('Failed to delete documents:', error)
        alert('Failed to delete documents. Please try again.')
      } finally {
        this.documentsForm.saving = false
      }
    },

    pollForDocumentThumbnails(attempts = 0) {
      const maxAttempts = 10
      const pollInterval = 3000

      const hasProcessingDocs = this.property?.documents?.some(doc => !doc.thumbnail_url && doc.content_type === 'application/pdf')

      if (!hasProcessingDocs || attempts >= maxAttempts || !this.open) {
        return
      }

      setTimeout(async () => {
        if (!this.open) return

        await this.fetchProperty(true)

        const stillProcessing = this.property?.documents?.some(doc => !doc.thumbnail_url && doc.content_type === 'application/pdf')
        if (stillProcessing && attempts < maxAttempts - 1) {
          this.pollForDocumentThumbnails(attempts + 1)
        }
      }, pollInterval)
    },

    // ==========================================
    // Real-time update handling
    // ==========================================

    handleDataUpdate(message) {
      if (!this.property || !this.open) return
      const pid = this.property.id

      switch (message.type) {
        case 'property':
          if (message.id === pid) {
            if (message.action === 'update') {
              // Merge broadcast data into property (broadcast data is lightweight)
              Object.assign(this.property, message.data)
              // Sync hero photo from broadcast's photo_url (broadcast uses photo_url, slideover uses hero_photo_url)
              if ('photo_url' in message.data) {
                this.property.hero_photo_url = message.data.photo_url
                this.property.photo_count = message.data.photo_count || this.property.photo_count
                // Photos changed (e.g. a delete) — drop the cached gallery URLs
                this._photoUrls = null
              }
            }
            if (message.action === 'destroy') {
              this.close()
            }
          }
          break

        case 'rent_guarantee_policy':
          if (message.data?.property_id === pid) this.fetchProperty(true)
          break

        case 'certificate':
          if (message.data?.property_id === pid) {
            // Compliance data is computed server-side, refetch
            this.fetchProperty(true)
          }
          break

        case 'inspection':
          if (message.data?.property?.id === pid) {
            this.handleInspectionUpdate(message)
          }
          break

        case 'meter':
          if (message.data?.property_id === pid) {
            this.handleMeterUpdate(message)
          }
          break

        case 'utility_supplier':
          if (message.data?.property_id === pid) {
            this.handleSupplierUpdate(message)
          }
          break

        case 'meter_reading':
          if (message.data?.property_id === pid || this.meterBelongsToProperty(message.data)) {
            this.fetchProperty(true)
          }
          break

        case 'key_bunch':
          if (message.data?.property_id === pid) {
            this.fetchProperty(true)
          }
          break

        case 'timeline_entry':
          if (message.data?.property_id === pid) {
            this.handleTimelineUpdate(message)
          }
          break

        case 'job':
          if (message.data?.property_id === pid) {
            this.handleJobUpdate(message)
          }
          break

        case 'tenancy':
          if (message.data?.property?.id === pid || message.data?.property_id === pid) {
            this.fetchProperty(true)
          }
          break

        case 'property_ownership':
          if (message.data?.property_id === pid) {
            this.fetchProperty(true)
          }
          break

        case 'audit_log':
          if (message.data?.auditable_type === 'Property' && message.data?.auditable_id === pid) {
            const d = message.data
            const auditEntry = {
              id: `audit-${d.id}`,
              type: 'audit',
              entry_type: 'audit',
              title: d.message,
              icon: 'ph-clock-counter-clockwise',
              color: 'gray',
              formatted_date: d.created_at ? new Date(d.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '',
              user_name: d.user || 'System',
              created_at: d.created_at
            }
            if (!this.property.timeline) this.property.timeline = []
            this.property.timeline.unshift(auditEntry)
          }
          break
      }
    },

    handleInspectionUpdate(message) {
      if (!this.property?.inspections) return

      switch (message.action) {
        case 'create':
          this.property.inspections.unshift(this.transformInspection(message.data))
          this.property.inspection_count++
          break
        case 'update': {
          const idx = this.property.inspections.findIndex(i => i.id === message.id)
          if (idx !== -1) {
            this.property.inspections[idx] = this.transformInspection(message.data)
          }
          break
        }
        case 'destroy':
          this.property.inspections = this.property.inspections.filter(i => i.id !== message.id)
          this.property.inspection_count--
          break
      }
    },

    transformInspection(data) {
      return {
        id: data.id,
        uuid: data.uuid,
        status: data.status,
        status_display: data.status ? data.status.charAt(0).toUpperCase() + data.status.slice(1) : null,
        scheduled_date: data.scheduled_date,
        scheduled_date_display: data.scheduled_date ? this.formatDateDisplay(data.scheduled_date) : null,
        completed_at: data.completed_at,
        inspector_name: data.inspector?.full_name,
        next_inspection_due_date: data.next_inspection_due_date,
        next_inspection_due_display: data.next_inspection_due_date ? this.formatDateDisplay(data.next_inspection_due_date) : null
      }
    },

    handleMeterUpdate(message) {
      if (!this.property?.meters) return

      switch (message.action) {
        case 'create':
          // Ignore a meter we already hold — the create may have been applied
          // locally before the broadcast reached us
          if (!this.property.meters.some(m => m.id === message.data.id)) {
            this.property.meters.push(message.data)
          }
          break
        case 'update': {
          const idx = this.property.meters.findIndex(m => m.id === message.id)
          if (idx !== -1) {
            this.property.meters[idx] = message.data
          }
          break
        }
        case 'destroy':
          this.property.meters = this.property.meters.filter(m => m.id !== message.id)
          break
      }
    },

    handleSupplierUpdate(message) {
      if (!this.property) return
      if (!this.property.utility_suppliers) this.property.utility_suppliers = []
      const list = this.property.utility_suppliers

      switch (message.action) {
        case 'create':
          // Ignore a record we already hold — the create may have been applied
          // locally before the broadcast reached us
          if (!list.some(s => s.id === message.data.id)) {
            list.push(message.data)
          }
          // The gap this suggestion filled no longer exists
          delete this.utilitySuggestions[message.data.utility_type]
          break
        case 'update': {
          const idx = list.findIndex(s => s.id === message.id)
          if (idx !== -1) {
            list[idx] = message.data
          }
          break
        }
        case 'destroy':
          this.property.utility_suppliers = list.filter(s => s.id !== message.id)
          break
      }
      this.sortUtilitySuppliers()
    },

    // Newest change first — keeps the client's idea of "current supplier"
    // aligned with the server's reverse-chronological order
    sortUtilitySuppliers() {
      const list = this.property?.utility_suppliers
      if (!list) return
      const key = s => (s.started_on || '0000') + '|' + (s.created_at || '')
      list.sort((a, b) => key(b).localeCompare(key(a)))
    },

    handleJobUpdate(message) {
      if (!this.property) return
      if (!this.property.jobs) this.property.jobs = []

      const jobData = {
        id: message.data.id || message.id,
        title: message.data.title,
        status: message.data.status,
        status_label: message.data.status_label,
        status_color: message.data.status_color,
        kind: message.data.kind,
        kind_label: message.data.kind_label,
        category: message.data.category,
        category_label: message.data.category_label,
        priority: message.data.priority,
        contractor_name: message.data.contractor_name,
        created_at: message.data.created_at,
        scheduled_at: message.data.scheduled_at,
        completed_at: message.data.completed_at,
        cancelled_at: message.data.cancelled_at
      }

      switch (message.action) {
        case 'create':
          this.property.jobs.unshift(jobData)
          this.property.job_count = (this.property.job_count || 0) + 1
          break
        case 'update': {
          const idx = this.property.jobs.findIndex(j => j.id === message.id)
          if (idx !== -1) {
            this.property.jobs[idx] = jobData
          }
          break
        }
        case 'destroy':
          this.property.jobs = this.property.jobs.filter(j => j.id !== message.id)
          this.property.job_count = Math.max(0, (this.property.job_count || 1) - 1)
          break
      }
    },

    meterBelongsToProperty(data) {
      if (!this.property?.meters) return false
      return this.property.meters.some(m => m.id === data?.meter_id)
    },

    async deleteReading(readingId) {
      if (!confirm('Are you sure you want to delete this reading?')) return

      try {
        const response = await fetch(`/properties/${this.property.id}/meter-readings/${readingId}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
            'Accept': 'application/json'
          }
        })

        if (!response.ok) {
          const data = await response.json()
          alert(data.errors?.join(', ') || 'Failed to delete reading')
        }
      } catch (error) {
        console.error('Error deleting reading:', error)
        alert('Failed to delete reading')
      }
    },

    handleTimelineUpdate(message) {
      if (!this.property?.timeline) return

      switch (message.action) {
        case 'create':
          // addNote() already unshifts its own entry optimistically — the
          // broadcast for it arrives a moment later, so dedupe by id or the
          // keyed x-for crashes on the duplicate
          if (!this.property.timeline.some(e => e.id === message.data.id)) {
            this.property.timeline.unshift(message.data)
            this.property.timeline_count++
          }
          break
        case 'update': {
          // Deleting a note (and completing a reminder) is a soft-delete —
          // active flips to false and broadcasts as an update. The server
          // only serves active entries, so mirror that here by removing it.
          if (message.data?.active === false) {
            this.property.timeline = this.property.timeline.filter(e => e.id !== message.id)
            this.property.timeline_count--
          } else {
            const idx = this.property.timeline.findIndex(e => e.id === message.id)
            if (idx !== -1) {
              this.property.timeline[idx] = message.data
            }
          }
          break
        }
        case 'destroy':
          this.property.timeline = this.property.timeline.filter(e => e.id !== message.id)
          this.property.timeline_count--
          break
      }
    },

    // ==========================================
    // Timeline / Notes
    // ==========================================

    async addNote() {
      if (!this.noteForm.content?.trim() || !this.property) return
      this.noteForm.saving = true

      try {
        const newEntry = await this.saveNote({ type: 'Property', id: this.property.id }, this.noteForm.content)
        if (!this.property.timeline) this.property.timeline = []
        if (!this.property.timeline.some(e => e.id === newEntry.id)) {
          this.property.timeline.unshift(newEntry)
        }
        this.noteForm.content = ''
      } catch (error) {
        console.error('Failed to add note:', error)
        alert(error.message || 'Failed to add note. Please try again.')
      } finally {
        this.noteForm.saving = false
      }
    },
    removeTimelineEntry(entryId) {
      if (this.property?.timeline) {
        this.property.timeline = this.property.timeline.filter(e => e.id !== entryId)
      }
    },

    async deleteNote(noteId) {
      if (!confirm('Delete this note?')) return
      try {
        await destroyNote(noteId)
        this.removeTimelineEntry(noteId)
      } catch (error) {
        console.error('Failed to delete note:', error)
        alert('Failed to delete note. Please try again.')
      }
    },

    // ==========================================
    // Inspection actions
    // ==========================================

    async createInspection() {
      try {
        const response = await fetch(`/properties/${this.property.id}/inspections.json`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({})
        })

        if (response.ok) {
          const data = await response.json()
          if (data.uuid) {
            window.location.href = `/inspections/${data.uuid}/form`
          }
        }
      } catch (error) {
        console.error('[PropertySlideover] Failed to create inspection:', error)
      }
    },

    // ==========================================
    // Edit Property modal — loaded dynamically
    // ==========================================

    async openEditPropertyModal(propertyId) {
      await ensureModalsLoaded()
      // Fetch the edit modal HTML with @property set server-side
      const container = document.getElementById('property-edit-modal')
      if (container) {
        // Show loading state
        container.innerHTML = '<div class="flex items-center justify-center py-20"><svg class="animate-spin h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg></div>'
        Alpine.store('modals').isPropertyEditModalOpen = true
      }

      try {
        const response = await fetch(`/properties/${propertyId}/edit_modal`, {
          headers: { 'Accept': 'text/html' }
        })
        if (response.ok) {
          const html = await response.text()
          // Replace the modal container content
          const wrapper = document.getElementById('property-edit-modal-wrapper')
          if (wrapper) {
            wrapper.innerHTML = html
          } else {
            // Create a wrapper and inject
            const div = document.createElement('div')
            div.id = 'property-edit-modal-wrapper'
            div.innerHTML = html
            document.body.appendChild(div)
          }
          Alpine.store('modals').isPropertyEditModalOpen = true
        }
      } catch (error) {
        console.error('[PropertySlideover] Failed to load edit modal:', error)
      }
    },

    // ==========================================
    // Manage Meters modal
    // ==========================================

    async openManageMetersModal(propertyId) {
      await ensureModalsLoaded()
      // Update the property ID on the manage meters modal
      const modal = document.querySelector('[x-data="manageMetersModal()"]')
      if (modal) {
        modal.dataset.propertyId = propertyId
        // Also update the Alpine component's propertyId and refresh meters
        if (modal._x_dataStack) {
          const component = modal._x_dataStack[0]
          if (component) {
            component.propertyId = propertyId
            // Copy, never alias: the modal pushes optimistically on save, and
            // sharing the array would land that meter in the slideover's own
            // list — which then counts it again when the broadcast arrives.
            component.meters = [ ...(this.property?.meters || []) ]
          }
        }
      }
      Alpine.store('modals').showManageMetersModal = true
    },

    // ==========================================
    // Manage Utility Suppliers modal
    // ==========================================

    async openManageSuppliersModal(propertyId, presetType = null) {
      await ensureModalsLoaded()
      const modal = document.querySelector('[x-data="manageUtilitySuppliersModal()"]')
      if (modal) {
        modal.dataset.propertyId = propertyId
        if (modal._x_dataStack) {
          const component = modal._x_dataStack[0]
          if (component) {
            component.propertyId = propertyId
            // Copy, never alias (see openManageMetersModal)
            component.suppliers = [ ...(this.property?.utility_suppliers || []) ]
            component.editingSupplier = null
            component.resetNewSupplier()
            if (presetType) component.newSupplier.utility_type = presetType
          }
        }
      }
      Alpine.store('modals').showManageUtilitySuppliersModal = true
    },

    // ==========================================
    // Landlord Ownership
    // ==========================================

    async openLandlordOwnershipModal() {
      await ensureModalsLoaded()
      window.dispatchEvent(new CustomEvent('open-landlord-ownership-modal', {
        detail: {
          propertyId: this.property.id,
          propertyAddress: this.property.full_address,
          landlords: this.property.landlords || []
        }
      }))
      Alpine.store('modals').isLandlordModalOpen = true
    },

    // ==========================================
    // Photo gallery — fetches photo URLs lazily on first open
    // ==========================================

    _photoUrls: null,

    async openPhotoGallery(startIndex = 0) {
      if (!this.property?.photo_count) return
      await ensureModalsLoaded()
      if (typeof window.openPhotoGallery !== 'function') return

      // If we already fetched URLs, use them
      if (this._photoUrls) {
        window.openPhotoGallery(this._photoUrls, startIndex)
        return
      }

      // Fetch photo URLs from server
      try {
        const response = await fetch(`/properties/${this.property.id}/photos.json`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
        if (response.ok) {
          this._photoUrls = await response.json()
          window.openPhotoGallery(this._photoUrls, startIndex)
        }
      } catch (error) {
        console.error('[PropertySlideover] Failed to fetch photos:', error)
      }
    },

    // ==========================================
    // Computed helpers
    // ==========================================

    // The Issues tab: the record's issues and renewals as the Maintenance list rows them
    get activeJobs() {
      return (this.property?.jobs || []).filter(r => r.status === 'open')
    },

    get completedJobs() {
      return (this.property?.jobs || []).filter(r => r.status !== 'open')
    },

    openIssueRow(row) {
      if (row.kind === 'renewal') window.dispatchEvent(new CustomEvent('open-issue-slideover', { detail: { renewal_id: row.id } }))
      else window.dispatchEvent(new CustomEvent('open-issue-slideover', { detail: { id: row.id } }))
    },

    issueToneClass(tone) {
      return tone === 'off' ? 'text-[#7D7D78]' : 'text-[#141413]'
    },

    get metersByType() {
      const meters = this.property?.meters || []
      return {
        gas: meters.filter(m => m.meter_type === 'gas'),
        water: meters.filter(m => m.meter_type === 'water'),
        electric: meters.filter(m => m.meter_type === 'electricity' || m.meter_type === 'electric')
      }
    },

    get activeMeterList() {
      return this.metersByType[this.activeMeterTab] || []
    },

    // Suppliers arrive reverse-chronological, so [0] is the current supplier
    // for a utility type and the rest are its history
    get suppliersByType() {
      const grouped = { gas: [], electricity: [], water: [], council_tax: [] }
      const list = this.property?.utility_suppliers || []
      list.forEach(s => {
        if (grouped[s.utility_type]) grouped[s.utility_type].push(s)
      })
      return grouped
    },

    currentSupplier(type) {
      return this.suppliersByType[type][0] || null
    },

    previousSuppliers(type) {
      return this.suppliersByType[type].slice(1)
    },

    supplierSubtext(supplier) {
      if (!supplier) return ''
      const parts = []
      if (supplier.started_on_display) parts.push(`Since ${supplier.started_on_display}`)
      if (supplier.notes) parts.push(supplier.notes)
      return parts.join(' · ')
    },

    toggleSupplierHistory(type) {
      this.expandedSupplierType = this.expandedSupplierType === type ? null : type
    },

    async fetchUtilitySuggestions() {
      if (!this.property) return
      const recorded = new Set((this.property.utility_suppliers || []).map(s => s.utility_type))
      if (recorded.has('water') && recorded.has('council_tax')) {
        this.utilitySuggestions = {}
        return
      }
      try {
        const response = await fetch(`/properties/${this.property.id}/utility-suggestions`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
        if (response.ok) {
          this.utilitySuggestions = await response.json()
        }
      } catch (error) {
        console.error('[PropertySlideover] Failed to fetch utility suggestions:', error)
      }
    },

    async confirmSuggestion(type) {
      const suggestion = this.utilitySuggestions[type]
      if (!suggestion || !this.property) return
      try {
        const response = await fetch(`/properties/${this.property.id}/utility-suppliers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            utility_supplier: {
              utility_type: type,
              supplier_name: suggestion.name
            }
          })
        })
        if (response.ok) {
          const data = await response.json()
          delete this.utilitySuggestions[type]
          // Apply locally; the broadcast dedupes against this
          this.handleSupplierUpdate({ action: 'create', data: data.supplier })
        }
      } catch (error) {
        console.error('[PropertySlideover] Failed to confirm suggestion:', error)
      }
    },

    // ==========================================
    // Utility methods
    // ==========================================

    formatDateLong(dateStr) {
      if (!dateStr) return '-'
      const date = dateStr.includes('T') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00')
      const day = date.getDate()
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      return `${day} ${months[date.getMonth()]} ${date.getFullYear()}`
    },

    formatCurrency(amount) {
      if (amount == null) return '—'
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount)
    },

    formatDateDisplay(isoDate) {
      if (!isoDate) return null
      const date = new Date(isoDate)
      const day = date.getDate()
      const suffix = ['th', 'st', 'nd', 'rd'][(day % 10 > 3 || Math.floor(day % 100 / 10) === 1) ? 0 : day % 10]
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${day}${suffix} ${months[date.getMonth()]} ${date.getFullYear()}`
    },

    getInitials(name) {
      if (!name) return '?'
      name = name.replace(/\([^)]*\)|\[[^\]]*\]/g, ' ')
      return name.split(' ').map(n => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
    },

    // Contact group headings and the bulk "Email both" pill follow one rule
    // shared with the tenancy and job slideovers — utils/contact_group.js.
    contactGroupLabel(contacts, one, many) { return contactGroup.groupLabel(contacts, one, many) },
    bulkEmailLabel(contacts) { return contactGroup.bulkEmailLabel(contacts) },
    emailGroup(contacts, type) { contactGroup.emailGroup(contacts, type, this.property?.full_address) },

    // Tile colours and icons come from the shared vocabulary in
    // utils/compliance_status.js, which the tenancy slideover reads too.
    complianceStatusColor(status) { return compliance.statusColor(status) },
    complianceIconColor(status) { return compliance.iconColor(status) },
    complianceIcon(status) { return compliance.icon(status) },
    complianceExpiryText(item) { return compliance.expiryText(item) },
    compliancePillClass(status) { return compliance.pillClass(status) },
    compliancePillLabel(status, words) { return compliance.pillLabel(status, words) },

    openEpcModal() {
      if (!window.openEpcModal) return
      const epc = this.property.epc
      const mapped = epc ? {
        current_energy_rating: epc.current_rating,
        certificate_number: epc.certificate_number,
        certificate_url: epc.certificate_url,
        full_certificate_pdf_url: epc.full_certificate_pdf_url,
        api_retrieved_at: epc.api_sourced ? true : null,
        user_override: !epc.api_sourced,
        date_expires: epc.date_expires,
        address: epc.address
      } : null
      window.openEpcModal(this.property.id, this.property.full_address, mapped)
    },

    async fetchEpcChart(propertyId) {
      try {
        const response = await fetch(`/properties/${propertyId}/epc_chart`, {
          headers: { 'Accept': 'text/html' }
        })
        if (response.ok) {
          const html = await response.text()
          // Extract just the SVG from the response
          const match = html.match(/<svg[\s\S]*<\/svg>/)
          this.epcChartHtml = match ? match[0] : ''
        }
      } catch (error) {
        console.error('[PropertySlideover] Failed to fetch EPC chart:', error)
      }
    },

    // The old tab names (and the ones other screens still dispatch) land on
    // the tabs that hold that content now
    resolveTab(tab) {
      const map = { utilities: 'details', meters: 'details', tenancies: 'lettings', instructions: 'lettings', valuation: 'lettings', jobs: 'issues' }
      return map[tab] || tab || 'overview'
    },

    // ---- Header ----

    // The facts in words under the address: "2 bed flat · unfurnished · Colin Wakefield · Ref PJ-0412"
    get headerFacts() {
      const p = this.property
      if (!p) return ''
      const beds = p.studio ? 'Studio' : (p.beds != null ? `${p.beds} bed` : null)
      const type = p.property_type ? String(p.property_type).toLowerCase() : null
      const size = [beds, type].filter(Boolean).join(' ')
      return [size || null, p.furnished ? String(p.furnished).toLowerCase() : null, p.landlord_names || null, p.reference ? `Ref ${p.reference}` : null]
        .filter(Boolean).join(' · ')
    },

    marketingPill() {
      const s = this.property?.marketing_status
      return pill({ available_to_let: 'green', under_negotiation: 'blue', let_stc: 'blue' }[s] || 'grey')
    },

    // ---- Stats: figure on the second line, state on the third ----

    get tenancy() { return this.property?.current_tenancy || null },

    // "paid on the 7th" under the rent
    rentDayWords() {
      const d = this.tenancy?.rent_due_day
      if (!d) return ''
      const suffix = ['th', 'st', 'nd', 'rd'][(d % 10 > 3 || Math.floor(d % 100 / 10) === 1) ? 0 : d % 10]
      return `paid on the ${d}${suffix}`
    },

    // "13 months" under "Let since"
    tenancyAgeWords() {
      const m = this.tenancy?.months
      if (m == null) return ''
      if (m < 1) return 'this month'
      if (m < 24) return `${m} month${m === 1 ? '' : 's'}`
      return `${Math.floor(m / 12)} years`
    },

    // When the property is empty, since when
    vacantSince() {
      const ended = (this.property?.tenancies || []).find(t => t.status === 'ended' && t.end_date_display)
      return ended ? ended.end_date_display : null
    },

    get activeInstruction() {
      return (this.property?.instructions || []).find(i => !['let', 'withdrawn', 'closed', 'cancelled'].includes(i.status)) || null
    },

    get keysOut() { return (this.property?.keys || []).filter(k => k.checked_out) },

    // The numbers on the tags, or the count when the bunches carry no number
    get keyNumbers() {
      const keys = this.property?.keys || []
      const numbers = keys.map(k => k.identifier).filter(Boolean)
      if (numbers.length) return numbers.join(', ')
      return keys.length ? String(keys.length) : ''
    },

    get oldestOpenDays() {
      const days = this.activeJobs.map(r => r.days_open).filter(d => d != null)
      return days.length ? Math.max(...days) : null
    },

    // The next inspection as a figure and a word: the date and how far off it is
    get nextInspection() {
      const n = this.property?.inspection_next
      if (!n) return { value: '—', sub: this.property?.inspections_managed === false ? 'not in the service' : 'nothing due' }
      if (n.state === 'in_progress') return { value: 'Now', sub: 'in progress' }
      const on = n.on ? new Date(n.on + 'T00:00:00') : null
      const value = on ? `${on.getDate()} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][on.getMonth()]}` : '—'
      if (n.state === 'booked') return { value, sub: 'booked', tone: 'blue' }
      if (n.state === 'overdue') return { value, sub: 'overdue', tone: 'red' }
      const weeks = on ? Math.round((on - new Date()) / (7 * 24 * 3600 * 1000)) : null
      return { value, sub: weeks == null ? '' : (weeks <= 0 ? 'this week' : weeks < 9 ? `in ${weeks} week${weeks === 1 ? '' : 's'}`: `in ${Math.round(weeks / 4.3)} months`) }
    },

    // ---- Overview rows ----

    balancePill() {
      const s = this.tenancy?.balance_status
      return pill({ in_arrears: 'red', money_owed: 'amber', in_credit: 'green', funds_held: 'green', clear: 'green' }[s] || 'grey')
    },

    balanceLabel() {
      const t = this.tenancy
      if (!t) return ''
      return t.balance_status === 'clear' ? 'Balance clear' : (t.balance_label || '')
    },

    // One row per policy in force in the Compliance list
    get policiesInForce() { return (this.property?.rent_guarantee_policies || []).filter(p => p.status !== 'ended') },
    get policiesEnded() { return (this.property?.rent_guarantee_policies || []).filter(p => p.status === 'ended') },

    policyState(p) {
      if (p.awaiting_end_of_tenancy_decision || p.vacant_without_decision) return { pill: pill('amber'), label: 'Decision due' }
      if (p.rent_mismatch) return { pill: pill('amber'), label: 'Rent differs' }
      if (p.pending_rent_change) return { pill: pill('amber'), label: 'Rent change due' }
      if (p.status === 'ending') return { pill: pill('grey'), label: 'Ending' }
      return { pill: pill('green'), label: 'In force' }
    },

    policyLine(p) {
      const parts = [p.provider, p.policy_number].filter(Boolean)
      if (p.status === 'ending' && p.ended_on_display) parts.push(`cancelled from ${p.ended_on_display}`)
      else if (p.period_ends_on_display) parts.push(`until ${p.period_ends_on_display}`)
      return parts.join(' · ')
    },

    // The landlord's terms of business, the same three states as the landlord card
    agreementState(ll) {
      const a = ll?.agency_agreement
      if (!a) return { pill: pill('red'), label: 'None on file', line: 'No terms on file' }
      if (a.status === 'sent') return { pill: pill('amber'), label: 'Awaiting signature', line: a.sent_at ? `Sent ${this.formatDateDisplay(a.sent_at)}` : 'Sent' }
      // :signed, and :uploaded (a signed copy on file; the date may not have been read)
      const v = a.version_number ? `v${a.version_number}` : null
      if (a.status === 'uploaded' && !a.signed_on) return { pill: pill('green'), label: 'On file', line: ['Uploaded, signature date not detected', v].filter(Boolean).join(' · ') }
      return { pill: pill('green'), label: 'Signed', line: [a.signer_name ? `Signed by ${a.signer_name}` : 'Signed', this.formatDateDisplay(a.signed_on), v].filter(Boolean).join(', ') }
    },

    keyPill(k) { return pill(k.overdue ? 'red' : 'amber') },

    issuePill(row) {
      if (row.overdue) return { cls: pill('red'), label: 'Overdue' }
      if (row.tone === 'late') return { cls: pill('amber'), label: 'Needs you' }
      return null
    },

    // ---- Lettings: tenancies, instructions and valuations in one list ----

    get lettings() {
      const p = this.property
      if (!p) return []
      const rows = []
      ;(p.tenancies || []).forEach(t => rows.push({
        key: `t-${t.id}`, kind: 'Tenancy', id: t.id, sort: t.start_date || '',
        title: t.tenant_names || 'No tenants',
        line: [t.start_date_display ? (t.end_date_display ? `${t.start_date_display} to ${t.end_date_display}` : `Since ${t.start_date_display}`) : null, t.service_type_display].filter(Boolean).join(' · '),
        rent: t.current_rent_amount,
        pill: pill({ active: 'green', upcoming: 'blue', ending: 'amber' }[t.status] || 'grey'), state: t.status_display,
        open: () => this.$dispatch('open-tenancy-slideover', { id: t.id })
      }))
      ;(p.instructions || []).forEach(i => rows.push({
        key: `i-${i.id}`, kind: 'Instruction', id: i.id, sort: i.live_at || i.available_from || i.created_at || '',
        title: i.service_type_display || 'No service type',
        line: [i.available_from_display ? `Available ${i.available_from_display}` : null, i.live_at_display ? `Live ${i.live_at_display}` : null].filter(Boolean).join(' · '),
        rent: i.price,
        pill: pill(i.published && !['let', 'withdrawn', 'closed', 'cancelled'].includes(i.status) ? 'blue' : 'grey'), state: i.status_display,
        open: () => this.$dispatch('open-instruction-slideover', { id: i.id })
      }))
      ;(p.valuations || []).forEach(v => rows.push({
        key: `v-${v.id}`, kind: 'Valuation', id: v.id, sort: v.valuation_at || v.created_at || '',
        title: v.appraised_rent ? `Appraised at ${this.formatCurrency(v.appraised_rent)} pcm` : (v.status === 'requested' ? 'New enquiry' : 'Valuation'),
        line: [v.valuation_at_display ? `Valued ${v.valuation_at_display}` : (v.created_at_display ? `Enquired ${v.created_at_display}` : null), v.source ? `via ${v.source}` : null, v.landlord_name].filter(Boolean).join(' · '),
        rent: null,
        pill: pill({ won: 'green', lost: 'grey', requested: 'blue', booked: 'amber' }[v.status] || 'blue'), state: v.status_display,
        open: () => this.$dispatch('open-valuation-slideover', { id: v.id })
      }))
      return rows.sort((a, b) => String(b.sort).localeCompare(String(a.sort)))
    },

    newValuation() {
      this.newMenuOpen = false
      const ll = this.property?.landlords?.[0]
      this.$dispatch('open-add-valuation-slideover', { prefill: { property_id: this.property.id, landlord_id: ll?.id || null } })
    },
    newInstruction() {
      this.newMenuOpen = false
      this.$dispatch('open-instruction-slideover', { prefill: { property_id: this.property.id } })
    },
    newTenancy() {
      this.newMenuOpen = false
      this.$dispatch('open-tenancy-slideover-create', { property_id: this.property.id, property_name: this.property.full_address, full_address: this.property.full_address })
    },

    inspectionPill(status) {
      return pill({ booked: 'blue', sent: 'green', ready_to_send: 'amber', processing: 'blue', submitted: 'blue' }[status] || 'grey')
    }
  }
}
